import {Stack, Typography, useTheme} from '@mui/material';
import {Drawer} from 'presentation/components/Drawer';
import {Filter} from 'presentation/components/Filter';
import {QueryParamsEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import React, {useEffect, useMemo, useState} from 'react';
import {AuthorizationOdontoStatusEnum} from '../../constants/authorizationOdontoStatus';
import {AuthorizationsOdontoFilterProps, FilterProps, OdontoFilterEnum} from './types';

const {clear, resultsFound, resultFound, filterAuthorizations} = translations['pt-br'].commons;
const {status: statusLabel, filtersTitle} = translations['pt-br'].privateFlow.requestAuthorizations;

export function AuthorizationsOdontoFilter({
	reset,
	totalItems,
	isLoadingItems,
}: AuthorizationsOdontoFilterProps) {
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {colors} = useTheme();
	const [openDrawerState, setOpenDrawerState] = useState(false);
	const [selectedFilter, setSelectedFilter] = useState<
		OdontoFilterEnum | AuthorizationOdontoStatusEnum
	>(OdontoFilterEnum.ALL);

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};
	const handleCloseDrawer = () => {
		setOpenDrawerState(false);
	};

	const {authorizationStatus} = SEARCH_PARAMS;

	function handleSelectFilter(type: string | OdontoFilterEnum | AuthorizationOdontoStatusEnum) {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.AUTHORIZATION_STATUS, type);
			return prev;
		});
	}

	function handleRemoveFilter() {
		setSelectedFilter(OdontoFilterEnum.ALL);
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.AUTHORIZATION_STATUS);
			return prev;
		});
	}

	const handleApplyFilter = () => {
		if (selectedFilter === authorizationStatus) {
			handleCloseDrawer();
			return;
		}

		handleSelectFilter(selectedFilter);
		if (reset) {
			reset();
		}
		handleCloseDrawer();
	};

	const handleClearFilter = () => {
		handleRemoveFilter();
		handleCloseDrawer();
	};

	useMemo(() => {
		if (SEARCH_PARAMS.authorizationStatus === OdontoFilterEnum.ALL) {
			handleRemoveFilter();
		}
	}, [SEARCH_PARAMS.authorizationStatus]);

	const filters: FilterProps[] = [
		{
			active: selectedFilter === OdontoFilterEnum.ALL,
			title: statusLabel.all,
			onClick: () => setSelectedFilter(OdontoFilterEnum.ALL),
			label: statusLabel.all,
			category: OdontoFilterEnum.ALL,
		},
		{
			active: selectedFilter === AuthorizationOdontoStatusEnum.FINISHED,
			title: statusLabel.finished,
			onClick: () => setSelectedFilter(AuthorizationOdontoStatusEnum.FINISHED),
			label: statusLabel.finished,
			category: AuthorizationOdontoStatusEnum.FINISHED,
		},
		{
			active: selectedFilter === AuthorizationOdontoStatusEnum.UNDER_ANALYSIS,
			title: statusLabel.underOdontoAnalysis,
			onClick: () => setSelectedFilter(AuthorizationOdontoStatusEnum.UNDER_ANALYSIS),
			label: statusLabel.underOdontoAnalysis,
			category: AuthorizationOdontoStatusEnum.UNDER_ANALYSIS,
		},
		{
			active: selectedFilter === AuthorizationOdontoStatusEnum.PENDING,
			title: statusLabel.pendingItems,
			onClick: () => setSelectedFilter(AuthorizationOdontoStatusEnum.PENDING),
			label: statusLabel.pendingItems,
			category: AuthorizationOdontoStatusEnum.PENDING,
		},
		{
			active: selectedFilter === AuthorizationOdontoStatusEnum.CANCELED,
			title: statusLabel.canceled,
			onClick: () => setSelectedFilter(AuthorizationOdontoStatusEnum.CANCELED),
			label: statusLabel.canceled,
			category: AuthorizationOdontoStatusEnum.CANCELED,
		},
		{
			active: selectedFilter === AuthorizationOdontoStatusEnum.REJECTED,
			title: statusLabel.rejected,
			onClick: () => setSelectedFilter(AuthorizationOdontoStatusEnum.REJECTED),
			label: statusLabel.rejected,
			category: AuthorizationOdontoStatusEnum.REJECTED,
		},
	];

	const categoryList = [
		{
			categoryTitle: filtersTitle,
			categoryItemsList: filters,
		},
	];

	useEffect(() => {
		if (!authorizationStatus) handleSelectFilter(OdontoFilterEnum.ALL);
	}, [authorizationStatus]);

	return (
		<Stack flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
			<Typography
				component={'span'}
				fontSize={'1rem'}
				fontWeight={700}
				color={colors.light.text.secondary}
				visibility={isLoadingItems ? 'hidden' : 'visible'}>
				{totalItems}
				<Typography
					component={'span'}
					fontSize={'1rem'}
					fontWeight={400}
					color={colors.light.text.secondary}
					textTransform={'lowercase'}
					ml={'4px'}>
					{totalItems === 1 ? resultFound : resultsFound}
				</Typography>
			</Typography>
			<Filter.Button
				customLabel={filterAuthorizations}
				onClick={handleOpenDrawer}
				count={authorizationStatus !== OdontoFilterEnum.ALL ? 1 : 0}
			/>
			<Drawer.Root
				open={openDrawerState}
				onOpen={handleOpenDrawer}
				onClose={handleCloseDrawer}>
				<Drawer.Header onClose={handleCloseDrawer} title="Filtros" />
				<Drawer.ContentListByCategory categoryList={categoryList} />
				<Drawer.Footer
					firstButtonTitle={clear}
					firstButtonOnClick={handleClearFilter}
					secondButtonTitle="Aplicar"
					secondButtonOnClick={handleApplyFilter}
				/>
			</Drawer.Root>
		</Stack>
	);
}

export type {AuthorizationsOdontoFilterProps, FilterProps};
